import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { requireFeature } from '@/common/utils/requireFeature';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Plus, Search } from 'lucide-react';
import { techSystemsService } from '@/features/tech-systems/services/tech-systems.service';
import {
  labelTicketPriority,
  labelTicketStatus,
} from '@/features/tech-systems/constants/labels';
import { PermissionGate } from '@/components/PermissionGate';
import { Permission } from '@/common/enums/permissions.enum';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Pagination } from '@/components/ui/pagination';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

const TICKETS_PER_PAGE = 20;

type TicketsSearch = {
  techSystemId?: string;
  create?: string;
};

export const Route = createFileRoute('/_authenticated/tech-systems/tickets/')({
  beforeLoad: () => requireFeature('TECH_SYSTEMS'),
  component: TechSystemsTicketsPage,
  validateSearch: (s: Record<string, unknown>): TicketsSearch => ({
    techSystemId: (s.techSystemId as string) || undefined,
    create: (s.create as string) || undefined,
  }),
});

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'open':
      return 'bg-blue-500/10 text-blue-700 dark:text-blue-300';
    case 'closed':
      return 'bg-muted text-muted-foreground';
    default:
      return 'bg-amber-500/10 text-amber-700 dark:text-amber-300';
  }
}

function TechSystemsTicketsPage() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { techSystemId: filterSystemId, create: createForSystemId } =
    Route.useSearch();

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState(
    filterSystemId ? 'active' : 'active',
  );
  const [systemFilter, setSystemFilter] = useState(filterSystemId ?? '');
  const [createOpen, setCreateOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [newSystemId, setNewSystemId] = useState(createForSystemId ?? '');

  useEffect(() => {
    if (createForSystemId) {
      setNewSystemId(createForSystemId);
      setCreateOpen(true);
    }
  }, [createForSystemId]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, systemFilter]);

  const { data: ticketsPage, isLoading } = useQuery({
    queryKey: [
      'tech-systems-tickets',
      search,
      page,
      statusFilter,
      systemFilter,
    ],
    queryFn: () =>
      techSystemsService.listTickets({
        search: search.trim() || undefined,
        page,
        limit: TICKETS_PER_PAGE,
        status: statusFilter === 'all' ? undefined : statusFilter,
        techSystemId: systemFilter || undefined,
      }),
  });

  const { data: systems = [] } = useQuery({
    queryKey: ['tech-systems'],
    queryFn: () => techSystemsService.list({ status: 'active' }),
  });

  const items = ticketsPage?.items ?? [];
  const total = ticketsPage?.total ?? 0;

  const createMutation = useMutation({
    mutationFn: () =>
      techSystemsService.createTicket({
        title: title.trim(),
        techSystemId: newSystemId,
      }),
    onSuccess: (ticket) => {
      queryClient.invalidateQueries({ queryKey: ['tech-systems-tickets'] });
      setCreateOpen(false);
      setTitle('');
      toast({ title: 'Ticket criado' });
      navigate({
        to: '/tech-systems/tickets/$ticketId',
        params: { ticketId: ticket.id },
      });
    },
  });

  const onCreateOpenChange = (o: boolean) => {
    setCreateOpen(o);
    if (!o) {
      setTitle('');
      setNewSystemId(createForSystemId ?? '');
    }
  };

  return (
    <PermissionGate permission={Permission.TECH_SYSTEMS_VIEW}>
      <div className="space-y-6 p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Tickets de suporte</h1>
            <p className="text-sm text-muted-foreground">
              Todos os tickets dos sistemas que você pode acessar
            </p>
          </div>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Novo ticket
          </Button>
        </div>

        <div className="flex flex-wrap items-end gap-4">
          <div className="relative min-w-[240px] flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Buscar por título, sistema ou cliente..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="min-w-[160px] space-y-1">
            <Label>Status</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Em aberto</SelectItem>
                <SelectItem value="open">Aberto</SelectItem>
                <SelectItem value="in_progress">Em andamento</SelectItem>
                <SelectItem value="waiting">Aguardando</SelectItem>
                <SelectItem value="resolved">Resolvido</SelectItem>
                <SelectItem value="closed">Fechado</SelectItem>
                <SelectItem value="all">Todos</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[200px] space-y-1">
            <Label>Sistema</Label>
            <Select
              value={systemFilter || '__all__'}
              onValueChange={(v) =>
                setSystemFilter(v === '__all__' ? '' : v)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos os sistemas</SelectItem>
                {systems.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Título</TableHead>
                <TableHead>Sistema</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Prioridade</TableHead>
                <TableHead>Abertura</TableHead>
                <TableHead>Fechamento</TableHead>
                <TableHead className="w-[80px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    Carregando...
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    Nenhum ticket encontrado.
                  </TableCell>
                </TableRow>
              ) : (
                items.map((row) => (
                  <TableRow key={row.ticket.id}>
                    <TableCell className="font-medium max-w-[200px] truncate">
                      {row.ticket.title}
                    </TableCell>
                    <TableCell>{row.systemName}</TableCell>
                    <TableCell>{row.clientName ?? '—'}</TableCell>
                    <TableCell>
                      <span
                        className={cn(
                          'rounded-full px-2 py-0.5 text-xs font-medium',
                          statusBadgeClass(row.ticket.status),
                        )}
                      >
                        {labelTicketStatus(row.ticket.status)}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">
                      {labelTicketPriority(row.ticket.priority)}
                    </TableCell>
                    <TableCell className="text-sm whitespace-nowrap">
                      {format(new Date(row.ticket.createdAt), 'dd/MM/yyyy', {
                        locale: ptBR,
                      })}
                    </TableCell>
                    <TableCell className="text-sm whitespace-nowrap">
                      {row.ticket.closedAt
                        ? format(new Date(row.ticket.closedAt), 'dd/MM/yyyy', {
                            locale: ptBR,
                          })
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <Button size="sm" variant="ghost" asChild>
                        <Link
                          to="/tech-systems/tickets/$ticketId"
                          params={{ ticketId: row.ticket.id }}
                        >
                          Abrir
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <Pagination
          total={total}
          page={page}
          limit={TICKETS_PER_PAGE}
          onPageChange={setPage}
        />

        <Dialog open={createOpen} onOpenChange={onCreateOpenChange}>
          <DialogContent size="sm" busy={createMutation.isPending}>
            <DialogHeader>
              <DialogTitle>Novo ticket</DialogTitle>
            </DialogHeader>
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                if (title.trim() && newSystemId && !createMutation.isPending) {
                  createMutation.mutate();
                }
              }}
            >
              <div className="space-y-1">
                <Label>Sistema</Label>
                <Select value={newSystemId} onValueChange={setNewSystemId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {systems.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="ticket-title">Título *</Label>
                <Input
                  id="ticket-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => onCreateOpenChange(false)}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={!title.trim() || !newSystemId}
                  loading={createMutation.isPending}
                >
                  Criar e abrir
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </PermissionGate>
  );
}
